import React from 'react';
import Image from 'frontastic/lib/image';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Tiempos } from 'fonts';
import { GenericSectionContent, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SECTION_SPACING } from '../../../helpers/rendering';

/**
 * Solis — the collection page's intro header: a full-bleed image beside a centred title and a
 * rich-text body that carries its own inline imagery (the range's logo lockup), which is what
 * separates it from Mesa. The CMS-driven equivalent of the hardcoded `product-list/collection`
 * block (see /au/collection/kiri/), minus that component's breadcrumb, video, card row and image
 * carousel - those are separate sections in V2, not part of this one.
 *
 * The `.image-container` class the copy hangs off is defined in styles/components/slider.css and
 * sizes those inline logos; `[&>p>img]` covers the same image once sanitize-html has wrapped it in
 * a paragraph rather than a div.
 *
 * Class-for-class the same markup as caroma's Solis, so the two storefronts stay in step.
 */
export default function SolisRendering({ content }: { content: GenericSectionContent }) {
  const { title, description, cards = [], cards_block_alignment, ctas, additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'SolisRendering');

  const image = cards[0]?.desktop_image;
  const isCardsLeft = cards_block_alignment === 'Left';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} relative left-full w-screen -translate-x-full lg:grid lg:grid-cols-12 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div
          id="advice-section"
          className={`flex flex-col items-center lg:col-span-12 lg:px-0 ${isCardsLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
        >
          {image?.url && (
            <div className="relative h-[20rem] w-full md:h-[46rem] lg:flex-1 lg:flex-grow xl:h-[32rem] middleresolution:h-[37rem] 2xl:h-[45rem]">
              <Image priority loading="eager" src={image.url} alt={image.description ?? title ?? ''} fill style={{ objectFit: 'cover' }} />
            </div>
          )}
          <div className="flex-1 flex-grow lg:px-48 2xl:px-144">
            {title && (
              <Typography as="h1" className={`relative pb-10 pt-10 text-center text-32 font-medium leading-10 ${Tiempos.className}`}>
                {title}
              </Typography>
            )}
            {description && (
              <div
                className="image-container px-24 text-center text-16 font-normal leading-6 text-[#333] md:text-18 lg:leading-8 xl:text-16 2xl:text-20 [&>div>img]:mb-10 [&>p>img]:mb-10"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
              />
            )}
            {ctas && ctas.length > 0 && (
              <div className="px-24">
                {ctas.map((cta: Cta, index: number) => (
                  <Link key={index} link={cta?.url ?? '#'} target={cta?.target || '_self'} rel="noopener noreferrer">
                    <Button className="m-auto mt-48 block w-full rounded-none border-2 border-[#2B2C39] bg-white px-18 py-16 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:px-32 md:py-16 lg:w-fit">
                      <Typography as="span" className="text-16 font-semibold leading-5 text-[#090909] lg:leading-6 xl:text-14 2xl:text-16">
                        {cta?.label}
                      </Typography>
                    </Button>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
